import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { useBedStatus } from './bed-status.queries';
import type { BedOccupancy } from './types';

const formatDateTime = (value: string) => new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  hour12: true, timeZone: 'Asia/Kolkata',
}).format(new Date(value));

const sourceLabel = (occupancy: BedOccupancy) => occupancy.source === 'ipd'
  ? 'IPD'
  : occupancy.source === 'dialysis'
    ? 'DIALYSIS'
    : 'DAYCARE';

const BedIcon = () => (
  <svg viewBox="0 0 64 46" aria-hidden="true">
    <circle cx="14" cy="15" r="7" />
    <path d="M5 35V8M5 28h51M56 28v10M21 17h24c6 0 11 5 11 11H21Z" />
  </svg>
);

export const BedStatusPage = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const statusQuery = useBedStatus(search);

  const occupancyHref = (occupancy: BedOccupancy | null) => {
    if (!occupancy?.sourceRecordId) return null;
    return occupancy.source === 'dialysis'
      ? `/dialysis/${occupancy.sourceRecordId}`
      : `/ipd-daycare/${occupancy.sourceRecordId}`;
  };

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const resetSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const errorMessage = statusQuery.error instanceof ApiError ? statusQuery.error.message : 'Bed status could not be loaded.';
  const bedCount = statusQuery.data?.groups.reduce((total, group) => total + group.beds.length, 0) ?? 0;

  return (
    <div className="page-stack bed-status-page">
      <div className="bed-status-titlebar"><h1>Bed Status</h1></div>

      <section className="bed-status-toolbar" aria-label="Bed status controls">
        <div className="bed-status-legend">
          <span><i className="bed-status-swatch ipd" />Used Bed (IPD Patient)</span>
          <span><i className="bed-status-swatch daycare-dialysis" />Used Bed (Daycare/Dialysis Patient)</span>
          <span><i className="bed-status-swatch free" />Free Bed</span>
        </div>
        <form className="bed-status-search" onSubmit={applySearch} role="search">
          <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search By Bed No..." aria-label="Search by bed number" />
          <button className="button button-secondary" type="submit" disabled={statusQuery.isFetching}>Search</button>
          <button className="button bed-reset-button" type="button" disabled={statusQuery.isFetching || (!search && !searchInput)} onClick={resetSearch}>Reset</button>
        </form>
      </section>

      {statusQuery.isLoading ? <LoadingState /> : null}
      {statusQuery.isError ? <ErrorState title="Unable to load Bed Status" message={errorMessage} action={{ label: 'Try again', onClick: () => void statusQuery.refetch() }} /> : null}
      {statusQuery.isSuccess && statusQuery.data.groups.length === 0 ? (
        <EmptyState
          title={search ? 'No matching bed' : 'No active beds configured'}
          message={search ? 'Try a different bed number or reset the search.' : 'Create active wards and beds in Settings to populate Bed Status.'}
          {...(search ? { action: { label: 'Reset search', onClick: resetSearch } } : {})}
        />
      ) : null}
      {statusQuery.isSuccess ? (
        <div className={`bed-status-groups${statusQuery.isFetching ? ' loading' : ''}`} aria-busy={statusQuery.isFetching}>
          {statusQuery.data.groups.map((group) => (
            <section className="bed-ward-section" key={group.id}>
              <h2>{group.name}</h2>
              <div className="bed-card-grid">
                {group.beds.map((bed) => (
                  <div className="bed-status-card-wrap" key={bed.id}>
                    <article
                      className={`bed-status-card ${bed.usageStatus}`}
                      title={bed.occupancy ? undefined : `Free bed: ${bed.name}`}
                      tabIndex={occupancyHref(bed.occupancy) ? 0 : undefined}
                      role={occupancyHref(bed.occupancy) ? 'link' : undefined}
                      aria-label={occupancyHref(bed.occupancy) ? `Open admission details for ${bed.name}` : undefined}
                      aria-describedby={bed.occupancy ? `bed-tooltip-${bed.id}` : undefined}
                      onClick={() => {
                        const href = occupancyHref(bed.occupancy);
                        if (href) navigate(href);
                      }}
                      onKeyDown={(event) => {
                        const href = occupancyHref(bed.occupancy);
                        if (href && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          navigate(href);
                        }
                      }}
                    >
                      <BedIcon />
                      <strong>{bed.name}</strong>
                    </article>
                    {bed.occupancy ? (
                      <div className="bed-patient-tooltip" id={`bed-tooltip-${bed.id}`} role="tooltip">
                        <span className="bed-patient-tooltip-type">{sourceLabel(bed.occupancy)} Patient</span>
                        <strong>{bed.occupancy.patientName}</strong>
                        <span>Patient No. {bed.occupancy.patientNumber}</span>
                        <span>{bed.occupancy.gender} | {bed.occupancy.ageDisplay}</span>
                        <span>Admitted {formatDateTime(bed.occupancy.admittedAt)}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
                {group.beds.length === 0 ? <span className="bed-ward-empty">No beds configured</span> : null}
              </div>
            </section>
          ))}
        </div>
      ) : null}
      {statusQuery.isSuccess ? <p className="bed-status-count">Showing {bedCount} active beds</p> : null}
    </div>
  );
};